import type { ActorRefFrom } from "xstate";
import type { Direction, Position } from "../types/grid.js";
import type { GridMap } from "../grid/GridMap.js";
import type { CameraController } from "../camera/CameraController.js";
import type { playerMachine } from "../../machines/playerMachine.js";
import {
  targetPosition,
  strafePosition,
  canMoveTo,
  blockedByEntity,
} from "../grid/CollisionDetector.js";
import { narrativeManager } from "../narrative/NarrativeManager.js";
import { SneakState } from "../party/SneakState.js";
import { devLog } from "../debug/devLog.js";
import { EventBus } from "../EventBus.js";

// ── Turn helpers ─────────────────────────────────────────────────────────────

// Direction enum values are radians: North=0, East=π/2, South=π, West=3π/2
const TWO_PI = Math.PI * 2;

function turnLeft(facing: Direction): Direction {
  return ((facing - Math.PI / 2 + TWO_PI) % TWO_PI) as Direction;
}

function turnRight(facing: Direction): Direction {
  return ((facing + Math.PI / 2) % TWO_PI) as Direction;
}

// ── Module singleton for UI-driven nav (U05) ─────────────────────────────────

let activeInputHandler: InputHandler | null = null;

/**
 * Returns the InputHandler bound to the active map, or `null` between
 * map loads. UI code (`NavCluster`) uses this to drive movement from
 * on-screen buttons without threading a ref through React.
 */
export function getActiveInputHandler(): InputHandler | null {
  return activeInputHandler;
}

// ── InputHandler ─────────────────────────────────────────────────────────────

/**
 * Translates keyboard events into grid-movement commands.
 *
 * Key mapping (classic blobber scheme):
 *   W / ArrowUp    → move forward
 *   S / ArrowDown  → move backward
 *   A              → strafe left
 *   D              → strafe right
 *   ArrowLeft / Q  → turn left
 *   ArrowRight / E → turn right
 *
 * Each move is rejected when the target cell is solid (`CollisionDetector`).
 * Movement is also gated on the camera tween completing so commands don't
 * queue up.
 */
export class InputHandler {
  private position: Position;
  private facing: Direction;

  private readonly grid: GridMap;
  private readonly camera: CameraController;
  private readonly playerActor: ActorRefFrom<typeof playerMachine>;
  private readonly onMove: ((pos: Position) => void) | undefined;

  constructor(
    startPosition: Position,
    startFacing: Direction,
    grid: GridMap,
    camera: CameraController,
    playerActor: ActorRefFrom<typeof playerMachine>,
    onMove?: (pos: Position) => void,
  ) {
    this.position = startPosition;
    this.facing = startFacing;
    this.grid = grid;
    this.camera = camera;
    this.playerActor = playerActor;
    this.onMove = onMove;

    window.addEventListener("keydown", this.onKeyDown);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    activeInputHandler = this;
  }

  /** Current grid cell — snapshot for save/load. */
  getPosition(): Position {
    return this.position;
  }

  /** Current facing — snapshot for save/load. */
  getFacing(): Direction {
    return this.facing;
  }

  /** Remove the event listener when the handler is no longer needed. */
  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    if (activeInputHandler === this) activeInputHandler = null;
  }

  // ── Public command API (U05) ──────────────────────────────────────────────
  //
  // UI on-screen nav buttons drive the same code paths as the keyboard.
  // Guards (camera-tweening, narrative-active, player-actor idle) are
  // applied here — same as the keydown handler — so the UI doesn't
  // duplicate that logic.

  commandMove(dir: "forward" | "backward"): void {
    if (!this.canAcceptInput()) return;
    this.tryMove(dir);
  }

  commandStrafe(side: "left" | "right"): void {
    if (!this.canAcceptInput()) return;
    this.tryStrafe(side);
  }

  commandTurn(side: "left" | "right"): void {
    if (!this.canAcceptInput()) return;
    this.doTurn(side);
  }

  private canAcceptInput(): boolean {
    if (this.camera.isTweening) {
      devLog("input:blocked", { reason: "camera-tweening" });
      return false;
    }
    if (narrativeManager.isActive()) {
      devLog("input:blocked", { reason: "narrative-active" });
      return false;
    }
    const snap = this.playerActor.getSnapshot();
    if (!snap.matches("idle") && !snap.matches("combat")) {
      devLog("input:blocked", { reason: "player-not-idle", state: snap.value });
      return false;
    }
    return true;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Sneak toggle is a party-level mode, not a move — it fires even
    // during tweens and combat so the player can preempt their next step.
    if (e.key === "c" || e.key === "C") {
      e.preventDefault();
      SneakState.toggle();
      return;
    }

    // Block input while a move/rotation tween is in progress.
    if (this.camera.isTweening) {
      devLog("input:blocked", { reason: "camera-tweening", key: e.key });
      return;
    }

    // Block movement input while dialogue is active.
    if (narrativeManager.isActive()) {
      devLog("input:blocked", { reason: "narrative-active", key: e.key });
      return;
    }

    // Block movement input while the player machine is not idle. `combat`
    // is allowed — ATB combat keeps the player free to reposition between
    // enemy attacks; the playerMachine.combat state has its own MOVE/ROTATE
    // transitions that fall back through `moving`/`rotating` to `idle`.
    const snap = this.playerActor.getSnapshot();
    if (!snap.matches("idle") && !snap.matches("combat")) {
      devLog("input:blocked", { reason: "player-not-idle", state: snap.value, key: e.key });
      return;
    }

    switch (e.key) {
      case "w":
      case "W":
      case "ArrowUp":
        e.preventDefault();
        this.tryMove("forward");
        break;

      case "s":
      case "S":
      case "ArrowDown":
        e.preventDefault();
        this.tryMove("backward");
        break;

      case "a":
      case "A":
        e.preventDefault();
        this.tryStrafe("left");
        break;

      case "d":
      case "D":
        e.preventDefault();
        this.tryStrafe("right");
        break;

      case "ArrowLeft":
      case "q":
      case "Q":
        e.preventDefault();
        this.doTurn("left");
        break;

      case "ArrowRight":
      case "e":
      case "E":
        e.preventDefault();
        this.doTurn("right");
        break;
    }
  };

  private tryMove(dir: "forward" | "backward"): void {
    const effectiveFacing: Direction =
      dir === "backward"
        ? (((this.facing + Math.PI) % (Math.PI * 2)) as Direction)
        : this.facing;

    const target = targetPosition(this.position, effectiveFacing);
    if (!canMoveTo(this.grid, target)) {
      if (blockedByEntity(this.grid, target)) {
        EventBus.emit("world:bump_entity", { targetCell: target });
      }
      return;
    }

    this.position = target;
    this.camera.moveTo(target.x, target.z);
    this.playerActor.send({ type: "MOVE", target });
    this.onMove?.(target);
  }

  private tryStrafe(side: "left" | "right"): void {
    const target = strafePosition(this.position, this.facing, side);
    if (!canMoveTo(this.grid, target)) {
      if (blockedByEntity(this.grid, target)) {
        EventBus.emit("world:bump_entity", { targetCell: target });
      }
      return;
    }

    this.position = target;
    this.camera.moveTo(target.x, target.z);
    this.playerActor.send({ type: "MOVE", target });
    this.onMove?.(target);
  }

  private doTurn(side: "left" | "right"): void {
    const newFacing = side === "left" ? turnLeft(this.facing) : turnRight(this.facing);
    this.facing = newFacing;
    this.camera.rotateTo(newFacing);
    this.playerActor.send({ type: "ROTATE", facing: newFacing });
  }
}
